import type { IHttpClient } from '@wix/http-client'
import { withFallback } from './utils'
import type { Member, GetMyMemberResponse, GetMemberRoleResponse } from './types'
import { Role } from './types'

type PerformFetch = <T = any>(url: string, options?: Partial<RequestInit>) => Promise<T>

export type MemberAndRole = { member: Member | undefined; role: Role }

export interface IMembersClient {
	// Waterfall: fetch the member, then (only if it has an id) fetch its role.
	getMemberAndRole(): Promise<MemberAndRole>
	// Parallel: fetch the member and the role for a known memberId concurrently.
	getMemberAndRoleParallel(memberId: string): Promise<MemberAndRole>
}

// Both strategies fall back gracefully — a failed member fetch yields undefined, a failed
// (or missing) role yields Role.MEMBER — so neither method ever rejects.
const fetchMemberSafely = (fetchMember: () => Promise<Member | undefined>) => withFallback(fetchMember, () => undefined)
const fetchRoleSafely = (fetchRole: () => Promise<Role>) => withFallback(fetchRole, () => Role.MEMBER)

// Waterfall: skip the role request entirely when there is no member to attach it to.
const resolveMemberAndRole = async (transport: MemberTransport): Promise<MemberAndRole> => {
	const member = await fetchMemberSafely(transport.fetchMember)
	if (!member?.id) {
		return { member, role: Role.MEMBER }
	}
	const role = await fetchRoleSafely(() => transport.fetchRole(member.id!))
	return { member, role }
}

// Parallel: the member id is already known, so member and role are fetched concurrently.
const resolveMemberAndRoleParallel = async (transport: MemberTransport, memberId: string): Promise<MemberAndRole> => {
	const [member, role] = await Promise.all([
		fetchMemberSafely(transport.fetchMember),
		fetchRoleSafely(() => transport.fetchRole(memberId)),
	])
	return { member, role }
}

type MembersClientDeps = {
	httpClient: IHttpClient
	getSignedInstance: () => string
	performFetch: PerformFetch
	useAmbassador: boolean
}

type MemberTransport = {
	fetchMember: () => Promise<Member | undefined>
	fetchRole: (id: string) => Promise<Role>
}

const restTransport = (deps: MembersClientDeps): MemberTransport => ({
	fetchMember: async () => {
		const res = await deps.performFetch<GetMyMemberResponse>('/_api/members/v1/members/my?fieldsets=FULL', {
			headers: { authorization: deps.getSignedInstance() },
		})
		return res.member
	},
	fetchRole: async (id) => {
		const res = await deps.performFetch<GetMemberRoleResponse>(`/_api/wix-sm-webapp/v1/members/${id}/role`, {
			headers: { authorization: deps.getSignedInstance() },
		})
		return res.role ?? Role.MEMBER
	},
})

export const createMembersClient = (deps: MembersClientDeps): IMembersClient => {
	const transport = restTransport(deps)
	return {
		getMemberAndRole: () => resolveMemberAndRole(transport),
		getMemberAndRoleParallel: (memberId) => resolveMemberAndRoleParallel(transport, memberId),
	}
}
